
export const MAX_VARINT_BYTES = 10;
/** Ceiling on a 64-bit LEB128 value. Also a hostile-input guard: without it a
 *  peer can send an unbounded run of continuation bytes and spin the decoder. */

export const MAX_STRING_BYTES = 4096;
/** Nothing legitimate on this protocol is longer. Chat is capped far below. */

export const MAX_COLLECTION = 65536;
/** Ceiling on any length-prefixed collection, so a forged count cannot make the
 *  decoder allocate arbitrarily. */

const MAX_U64 = (1n << 64n) - 1n;

/** Base for malformed wire data. */
export class CodecError extends Error {
  constructor(message: string) {
    super(message);
    this.name = new.target.name;
  }
}

/** The buffer ended mid-value. */
export class Truncated extends CodecError {}

/** The bytes are well-formed but the value is out of contract. */
export class Malformed extends CodecError {}

// Shared across every Writer/Reader: constructing these is not free and they hold
// no per-call state.
const UTF8_ENCODER = new TextEncoder();
// `fatal: false` deliberately: a peer sending invalid UTF-8 should get a mangled
// name, not tear down the connection's decode loop. Mirrors Python's
// `errors="replace"`.
const UTF8_DECODER = new TextDecoder("utf-8", { fatal: false });

// --------------------------------------------------------------------- writing

/** Append-only byte builder. */
export class Writer {
  private buf: Uint8Array;
  private view: DataView;
  private len = 0;

  constructor(capacity = 64) {
    this.buf = new Uint8Array(capacity);
    this.view = new DataView(this.buf.buffer);
  }

  get length(): number {
    return this.len;
  }

  /** A copy of the bytes written so far. Copied, not a view, so a later write
   *  cannot mutate a frame the caller already handed to the socket. */
  bytes(): Uint8Array {
    return this.buf.slice(0, this.len);
  }

  private reserve(extra: number): void {
    if (this.len + extra <= this.buf.length) return;
    let capacity = this.buf.length * 2;
    while (capacity < this.len + extra) capacity *= 2;
    const grown = new Uint8Array(capacity);
    grown.set(this.buf.subarray(0, this.len));
    this.buf = grown;
    this.view = new DataView(grown.buffer);
  }

  private push(byte: number): void {
    this.reserve(1);
    this.buf[this.len++] = byte;
  }

  // -- primitives

  u8(value: number): this {
    if (!Number.isInteger(value) || value < 0 || value > 0xff) {
      throw new Malformed(`u8 out of range: ${value}`);
    }
    this.push(value);
    return this;
  }

  u16(value: number): this {
    if (!Number.isInteger(value) || value < 0 || value > 0xffff) {
      throw new Malformed(`u16 out of range: ${value}`);
    }
    this.reserve(2);
    this.view.setUint16(this.len, value, true);
    this.len += 2;
    return this;
  }

  u32(value: number): this {
    if (!Number.isInteger(value) || value < 0 || value > 0xffffffff) {
      throw new Malformed(`u32 out of range: ${value}`);
    }
    this.reserve(4);
    this.view.setUint32(this.len, value, true);
    this.len += 4;
    return this;
  }

  varint(value: number): this {
    if (!Number.isInteger(value)) {
      throw new Malformed(`varint is not an integer: ${value}`);
    }
    if (value < 0) {
      throw new Malformed(`varint is unsigned, got ${value}`);
    }
    if (!Number.isSafeInteger(value)) {
      // Rounding here would put a number on the wire that the sender never had.
      throw new Malformed(`varint ${value} exceeds the exact integer range`);
    }
    // Division rather than `>>>`: bitwise operators truncate to 32 bits, which
    // would silently corrupt anything above 2^32.
    let rest = value;
    while (rest >= 0x80) {
      this.push((rest % 0x80) | 0x80);
      rest = Math.floor(rest / 0x80);
    }
    this.push(rest);
    return this;
  }

  /** The full unsigned 64-bit varint domain, for values a number cannot hold. */
  varintBig(value: bigint): this {
    if (value < 0n) throw new Malformed(`varint is unsigned, got ${value}`);
    if (value > MAX_U64) throw new Malformed(`varint exceeds 64 bits: ${value}`);
    let rest = value;
    while (rest >= 0x80n) {
      this.push(Number(rest & 0x7fn) | 0x80);
      rest >>= 7n;
    }
    this.push(Number(rest));
    return this;
  }

  /** Zigzag-encoded signed varint: small magnitudes cost one byte. */
  svarint(value: number): this {
    if (!Number.isSafeInteger(value)) {
      throw new Malformed(`svarint out of exact integer range: ${value}`);
    }
    // Zigzag doubles the magnitude, so a value safe on its own can encode to one
    // that is not; check the encoded form rather than the input.
    const zigzag = value < 0 ? -2 * value - 1 : 2 * value;
    if (!Number.isSafeInteger(zigzag)) {
      throw new Malformed(`svarint ${value} does not zigzag exactly`);
    }
    return this.varint(zigzag);
  }

  svarintBig(value: bigint): this {
    return this.varintBig(value < 0n ? -2n * value - 1n : 2n * value);
  }

  /** Named for the schema's `Kind.BOOL`, which the generator spells `bool`. */
  bool(value: boolean): this {
    return this.u8(value ? 1 : 0);
  }

  /** Parity with the Python writer's method name. */
  boolean(value: boolean): this {
    return this.bool(value);
  }

  raw(data: Uint8Array): this {
    this.reserve(data.length);
    this.buf.set(data, this.len);
    this.len += data.length;
    return this;
  }

  string(value: string): this {
    const encoded = UTF8_ENCODER.encode(value);
    if (encoded.length > MAX_STRING_BYTES) {
      throw new Malformed(`string of ${encoded.length} bytes exceeds cap`);
    }
    this.varint(encoded.length);
    return this.raw(encoded);
  }

  bits(value: number, width: number): this {
    if (!Number.isInteger(value) || value < 0 || value >= 2 ** width) {
      throw new Malformed(`value ${value} does not fit in ${width} bits`);
    }
    return this.varint(value);
  }
}

// --------------------------------------------------------------------- reading

export class Reader {
  private readonly data: Uint8Array;
  private readonly view: DataView;
  private pos: number;

  constructor(data: Uint8Array, offset = 0) {
    this.data = data;
    this.view = new DataView(data.buffer, data.byteOffset, data.byteLength);
    this.pos = offset;
  }

  get position(): number {
    return this.pos;
  }

  get remaining(): number {
    return this.data.length - this.pos;
  }

  get exhausted(): boolean {
    return this.pos >= this.data.length;
  }

  private need(count: number): void {
    if (this.remaining < count) {
      throw new Truncated(`need ${count} bytes, ${this.remaining} remain`);
    }
  }

  // -- primitives

  u8(): number {
    this.need(1);
    return this.data[this.pos++];
  }

  u16(): number {
    this.need(2);
    const value = this.view.getUint16(this.pos, true);
    this.pos += 2;
    return value;
  }

  u32(): number {
    this.need(4);
    const value = this.view.getUint32(this.pos, true);
    this.pos += 4;
    return value;
  }

  varint(): number {
    // Fast path: seven bytes carry 49 bits, which a double holds exactly, so the
    // common case never allocates a BigInt.
    const start = this.pos;
    let result = 0;
    let scale = 1;
    for (let i = 0; i < 7; i++) {
      this.need(1);
      const byte = this.data[this.pos++];
      result += (byte & 0x7f) * scale;
      if ((byte & 0x80) === 0) return result;
      scale *= 0x80;
    }
    // Wider than 49 bits: redo it exactly, then refuse anything a number cannot
    // represent rather than handing back a rounded value.
    this.pos = start;
    const wide = this.varintBig();
    if (wide > BigInt(Number.MAX_SAFE_INTEGER)) {
      throw new Malformed(`varint ${wide} exceeds the exact integer range`);
    }
    return Number(wide);
  }

  /** The full unsigned 64-bit varint domain. */
  varintBig(): bigint {
    let result = 0n;
    let shift = 0n;
    for (let i = 0; i < MAX_VARINT_BYTES; i++) {
      this.need(1);
      const byte = this.data[this.pos++];
      result |= BigInt(byte & 0x7f) << shift;
      if ((byte & 0x80) === 0) return result;
      shift += 7n;
    }
    throw new Malformed(`varint longer than ${MAX_VARINT_BYTES} bytes`);
  }

  svarint(): number {
    const raw = this.varint();
    // `>> 1` and `& 1` are 32-bit in JavaScript; arithmetic keeps the full range.
    const half = Math.floor(raw / 2);
    return raw % 2 === 1 ? -(half + 1) : half;
  }

  svarintBig(): bigint {
    const raw = this.varintBig();
    return (raw >> 1n) ^ -(raw & 1n);
  }

  bool(): boolean {
    return this.u8() !== 0;
  }

  boolean(): boolean {
    return this.bool();
  }

  raw(count: number): Uint8Array {
    if (!Number.isInteger(count) || count < 0) {
      throw new Malformed(`negative length ${count}`);
    }
    this.need(count);
    const value = this.data.slice(this.pos, this.pos + count);
    this.pos += count;
    return value;
  }

  string(): string {
    const length = this.varint();
    if (length > MAX_STRING_BYTES) {
      throw new Malformed(`string length ${length} exceeds cap`);
    }
    return UTF8_DECODER.decode(this.raw(length));
  }

  bits(width: number): number {
    const value = this.varint();
    if (value >= 2 ** width) {
      throw new Malformed(`value ${value} does not fit in ${width} bits`);
    }
    return value;
  }

  /** Read a length prefix, refusing counts that could be an allocation bomb. */
  collectionLength(): number {
    const length = this.varint();
    if (length > MAX_COLLECTION) {
      throw new Malformed(`collection length ${length} exceeds cap`);
    }
    // A collection cannot be longer than the bytes left to hold it, even at one
    // byte per element. This catches a forged length before we allocate for it.
    if (length > this.remaining) {
      throw new Truncated(
        `collection of ${length} cannot fit in ${this.remaining} bytes`,
      );
    }
    return length;
  }
}

// ------------------------------------------------------------------ change mask

export class ChangeMask {
  value: number;

  constructor(value = 0) {
    this.value = value;
  }

  set(bit: number): this {
    // `1 << bit` wraps at 32 bits, so the bit is set by addition instead: a delta
    // struct with more than 31 optional fields must still round-trip.
    if (!this.has(bit)) this.value += 2 ** bit;
    return this;
  }

  has(bit: number): boolean {
    return Math.floor(this.value / 2 ** bit) % 2 === 1;
  }

  get empty(): boolean {
    return this.value === 0;
  }

  write(w: Writer): void {
    w.varint(this.value);
  }

  static read(r: Reader): ChangeMask {
    return new ChangeMask(r.varint());
  }
}
